import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Dispatch, useMemo } from 'react';
import { useState } from 'react';
import { SetStateAction } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'use-lodash-debounce';

import useAxiosHook from '../../../hooks/useAxiosHook';
import useHaveValuesChanged from '../../../hooks/useHaveValuesChanged';
import useSearchQueryDropdownParam from '../../../hooks/useSearchQueryDropdownParam';
import useSearchQueryMultiSelectParam from '../../../hooks/useSearchQueryMultiSelectParam';
import useSearchQueryParam from '../../../hooks/useSearchQueryParam';
import { WithPagination } from '../../../types/api';
import { LabelValue } from '../../../types/options';
import { debounceTimeout } from '../../../utils/constants/misc';
import { tryInt } from '../../../utils/helpers/parse';
import {
  getSearchQueryParam,
  tryMultiSelectParam,
} from '../../../utils/helpers/searchQuery';
import FiltersCounter from '../Components/Filters/FiltersCounter';
import { OAuthClient } from '../OAuthClients/OAuthClients.functions';

function useTableFilters(
  page: number,
  setPage: Dispatch<SetStateAction<number>>,
  limit: number
) {
  const { t } = useTranslation();
  const location = useLocation();
  const [nameFilter, setNameFilter] = useState(
    () => getSearchQueryParam(location.search, 'name') ?? ''
  );
  const [uriFilter, setUriFilter] = useState(
    () => getSearchQueryParam(location.search, 'uri') ?? ''
  );
  const [clientTypeFilter, setClientTypeFilter] = useState(
    () => tryInt(getSearchQueryParam(location.search, 'clientType')) ?? null
  );
  const [rolesFilter, setRolesFilter] = useState(() => {
    const value = getSearchQueryParam(location.search, 'role') ?? null;

    return value
      ? tryMultiSelectParam(value)?.map((o) => Number(o)) ?? null
      : null;
  });

  const debouncedNameFilter = useDebounce(nameFilter, debounceTimeout);
  const debouncedUriFilter = useDebounce(uriFilter, debounceTimeout);

  const { data: oAuthClientsData, isLoading: isOAuthClientsLoading } =
    useAxiosHook<WithPagination<OAuthClient[]>>('/oauthclients?limit=0');

  const clientTypeOptions = useMemo<LabelValue<number>[]>(
    () =>
      oAuthClientsData?.data.map((c) => ({
        label: c.client_name,
        value: c.id,
      })) ?? [],
    [oAuthClientsData?.data]
  );

  const rolesOptions = useMemo<LabelValue<number>[]>(
    () => [
      { label: 'Administration & Management', value: 1 },
      { label: 'Cash Desk', value: 2 },
      { label: 'Courier', value: 3 },
      { label: 'Warehouse', value: 4 },
      { label: 'Contact Center', value: 5 },
      { label: 'Sales & Marketing', value: 6 },
      { label: 'Transport (Courier)', value: 7 },
      { label: 'Finance', value: 8 },
      { label: 'Customer', value: 100 },
    ],
    []
  );

  const filtersArr = useMemo(() => [], []);

  const haveFiltersChanged = useHaveValuesChanged(filtersArr);

  useEffect(() => {
    if (!setPage) {
      return;
    }
    setPage(1);
  }, [
    setPage,
    debouncedNameFilter,
    debouncedUriFilter,
    clientTypeFilter,
    rolesFilter,
  ]);

  useSearchQueryParam('name', debouncedNameFilter);
  useSearchQueryParam('uri', uriFilter);

  useSearchQueryDropdownParam(
    'clientType',
    clientTypeFilter,
    setClientTypeFilter,
    clientTypeOptions
  );

  useSearchQueryMultiSelectParam('role', rolesFilter);

  const httpFiltersObj = useMemo(() => {
    return {
      page: haveFiltersChanged ? 1 : page,
      limit,
      name: debouncedNameFilter,
      uri: debouncedUriFilter,
      client_type: clientTypeFilter,
      roles: rolesFilter,
    };
  }, [
    clientTypeFilter,
    debouncedNameFilter,
    debouncedUriFilter,
    haveFiltersChanged,
    limit,
    page,
    rolesFilter,
  ]);

  const basicFiltersActiveFilterCount = Object.values({
    nameFilter,
    uriFilter,
    clientTypeFilter,
    rolesFilter,
  }).filter(Boolean).length;

  const filters = useMemo<JSX.Element>(
    () => (
      <Accordion multiple activeIndex={[0]}>
        <AccordionTab
          disabled
          headerTemplate={
            <FiltersCounter
              description={t('Basic filters')}
              counter={basicFiltersActiveFilterCount}
            />
          }
        >
          <div className="sidebar_filter">
            <label htmlFor="filter_name">{t('Name')}</label>
            <InputText
              id="filter_name"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>

          <div className="sidebar_filter">
            <label htmlFor="filter_uri">{t('URI')}</label>
            <InputText
              id="filter_uri"
              value={uriFilter}
              onChange={(e) => setUriFilter(e.target.value)}
            />
          </div>

          <div className="sidebar_filter">
            <label htmlFor="filter_clientType">{t('Client type')}</label>
            <Dropdown
              id="filter_clientType"
              value={clientTypeFilter}
              options={clientTypeOptions}
              onChange={(e) => setClientTypeFilter(e.value)}
              disabled={isOAuthClientsLoading}
              showClear
              filter
            />
          </div>

          <div className="sidebar_filter">
            <label htmlFor="filter_roles">{t('Roles')}</label>
            <MultiSelect
              inputId="filter_roles"
              value={rolesFilter}
              options={rolesOptions}
              maxSelectedLabels={0}
              selectedItemsLabel={t('{0} selected')}
              filter
              showClear
              onChange={(e) => {
                setRolesFilter(e.target.value);
              }}
            />
          </div>
        </AccordionTab>
      </Accordion>
    ),
    [
      clientTypeFilter,
      clientTypeOptions,
      isOAuthClientsLoading,
      basicFiltersActiveFilterCount,
      nameFilter,
      rolesFilter,
      rolesOptions,
      t,
      uriFilter,
    ]
  );

  const headerFiltersCount = useMemo(
    () => basicFiltersActiveFilterCount,
    [basicFiltersActiveFilterCount]
  );

  function resetAllFilters() {
    setNameFilter('');
    setUriFilter('');
    setClientTypeFilter(null);
    setRolesFilter(null);
  }

  return {
    filters,
    resetAllFilters,
    httpFiltersObj,
    headerFiltersCount,
  };
}

export default useTableFilters;
